import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../../../contexts/AuthContext';
import { useUserProfileContext } from '../../../../contexts/UserProfileContext';
import { useToast } from '../../../../contexts/ToastContext';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useTheme } from '../../../../ThemeContext';
import { authService } from '../../../../services/auth.service';
import { useBusinessSetupStatus } from '../../../../hooks/useBusinessSetupStatus';
import { ProfileType } from '../../../../types/enums';
import LoadingOverlay from '../../../LoadingOverlay/LoadingOverlay';
import ProfileSwitcher from '../../../ProfileSwitcher/ProfileSwitcher';
import styles from './menu.module.css';

interface MenuComponentProps {
    onSettingsClick?: () => void;
    onFaqClick?: () => void;
}

const Menu: React.FC<MenuComponentProps> = ({
    onSettingsClick = () => { },
    onFaqClick = () => { },
}) => {
    const { clearTokens, userType } = useAuthContext();
    const { profile, loading } = useUserProfileContext();
    const { showToast } = useToast();
    const { language, setLanguage, t } = useLanguage();
    const { theme, setTheme } = useTheme();
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    // AB#1675 — surface "My Business" with a SET UP highlight while incomplete.
    const { isIncomplete } = useBusinessSetupStatus();
    const showMyBusinessLink = userType === ProfileType.SalonOwner;

    const defaultAvatar = '/assets/icons/avatar.png';

    const handleMyBusinessClick = () => {
        setIsOpen(false);
        navigate('/my-business');
    };

    const handleToggle = () => {
        setIsOpen(!isOpen);
    };

    const handleLogout = async () => {
        try {
            setIsLoggingOut(true);
            const success = await authService.logout();
            if (success) {
                clearTokens();
            } else {
                showToast('Logout failed. Please try again.', 'error');
            }
        } catch {
            showToast('Logout failed. Please try again.', 'error');
        } finally {
            setIsLoggingOut(false);
        }
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
            setIsOpen(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const handleEnglish = () => {
        setLanguage('en');
        showToast(t('toast.languageSet'), 'info');
    };

    const handleArabic = () => {
        setLanguage('ar');
        showToast(t('toast.languageSetAr'), 'info');
    };

    const handleLightTheme = () => {
        setTheme('light');
        showToast(t('toast.themeSetLight'), 'info');
    };

    const handleDarkTheme = () => {
        setTheme('dark');
        showToast(t('toast.themeSetDark'), 'info');
    };

    const handleThemeKey = (e: React.KeyboardEvent<HTMLSpanElement>, mode: 'light' | 'dark') => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (mode === 'light') handleLightTheme();
            else handleDarkTheme();
        }
    };

    return (
        <>
            {/* O1 (AB#2199, SO-43) — the profile load no longer blocks the whole portal
                behind a full-screen "Loading user info…" modal on every page load; the
                menu just shows its avatar until the name arrives. Logging out still
                blocks (nothing else may run while the session ends). */}
            {isLoggingOut && (
                <LoadingOverlay message="Logging out..." />
            )}
            <div className={styles.menuContainer} ref={menuRef}>
                <button
                    className={`${styles.menuTrigger} arrowUpDown ${isOpen ? 'isOpen' : ''}`}
                    onClick={handleToggle}
                    aria-expanded={isOpen}
                    aria-haspopup="true"
                    aria-label={`Profile menu${profile?.fullName ? `, ${profile.fullName}` : ''}`}
                    aria-busy={loading || undefined}
                    onKeyDown={(e) => { if (e.key === 'Escape' && isOpen) { setIsOpen(false); } }}
                >
                    <img src={defaultAvatar} alt="" />
                </button>
                {isOpen && (
                    <div className={styles.menuContent} role="menu" aria-label="Profile options">
                        <div className={styles.menuProfile}>
                            <img src={defaultAvatar} alt="Profile" />
                            <div className={styles.profileDetails}>
                                <div className={styles.profileName}>{profile?.fullName || 'User'}</div>
                                <span
                                    className={styles.profileSettings}
                                    onClick={() => { setIsOpen(false); onSettingsClick(); }}
                                >
                                    {t('menu.accountSettings')}
                                </span>
                            </div>
                        </div>

                        {/* AB#1675 — My Business entry point. Highlighted with SET UP tag while
                            the salon owner's business setup is still incomplete. After all
                            sections are filled, the highlight goes away and the link sits as
                            a normal item. */}
                        {showMyBusinessLink && (
                            <button
                                type="button"
                                role="menuitem"
                                className={`${styles.myBusinessLink} ${isIncomplete ? styles.myBusinessHighlighted : ''}`}
                                onClick={handleMyBusinessClick}
                            >
                                <span className={styles.myBusinessIcon} aria-hidden="true">🏪</span>
                                <span className={styles.myBusinessLabel}>My Business</span>
                                {isIncomplete && (
                                    <span className={styles.myBusinessTag}>SET UP</span>
                                )}
                            </button>
                        )}

                        {/* Auth v2 profile switcher (AB#1435) — renders itself only when the
                            feature flag is on AND the user has >1 surface-able profile. */}
                        <ProfileSwitcher onClose={() => setIsOpen(false)} />

                        <span className={styles.languagesTitle}>{t('menu.theme')}</span>
                        <div className={styles.menuLanguages}>
                            <span
                                role="menuitem"
                                tabIndex={0}
                                className={theme === 'light' ? styles.activeLanguage : styles.inactiveLanguage}
                                onClick={handleLightTheme}
                                onKeyDown={(e) => handleThemeKey(e, 'light')}
                            >
                                {t('menu.themeLight')}
                            </span>
                            <span
                                role="menuitem"
                                tabIndex={0}
                                className={theme === 'dark' ? styles.activeLanguage : styles.inactiveLanguage}
                                onClick={handleDarkTheme}
                                onKeyDown={(e) => handleThemeKey(e, 'dark')}
                            >
                                {t('menu.themeDark')}
                            </span>
                        </div>

                        <span className={styles.languagesTitle}>{t('menu.languages')}</span>
                        <div className={styles.menuLanguages}>
                            <span
                                className={language === 'en' ? styles.activeLanguage : styles.inactiveLanguage}
                                onClick={handleEnglish}
                            >
                                English
                            </span>
                            <span
                                className={language === 'ar' ? styles.activeLanguage : styles.inactiveLanguage}
                                onClick={handleArabic}
                            >
                                العربية
                            </span>
                        </div>
                        <div className={styles.menuDivider}></div>
                        <span role="menuitem" tabIndex={0}>
                            <span className={styles.menuItem} onClick={() => { setIsOpen(false); onFaqClick(); }}>{t('menu.termsAndConditions')}</span>
                        </span>
                        <span role="menuitem" tabIndex={0}>
                            <span className={styles.menuItem} onClick={() => { setIsOpen(false); handleLogout(); }}>{t('menu.logout')}</span>
                        </span>
                    </div>
                )}
            </div>
        </>
    );
};

export default Menu;
